import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Check, FileText, Sparkles, ArrowRight, Eye, X, IdCard } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SignaturePad from "./SignaturePad";
import { FileSlot } from "./LicenseUploadForm";

/**
 * ContractStep — final step before Stripe.
 * Loads OCR-extracted license data + booking summary, pre-fills the rental contract form,
 * lets user complete missing fields (residence, fiscal code, ID document), draw a signature,
 * accept terms, then POSTs to /bookings/{id}/contract which generates a signed PDF
 * and opens the Stripe gate.
 */
export default function ContractStep({ bookingId, signed, setSigned, onProceed, submitting, total, t }) {
  const [loading, setLoading] = useState(true);
  const [prefilling, setPrefilling] = useState(false);
  const [submittingContract, setSubmittingContract] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPages, setPreviewPages] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    family_name: "",
    given_names: "",
    birthplace: "",
    birthdate: "",
    residence_street: "",
    residence_number: "",
    residence_city: "",
    residence_zip: "",
    residence_province: "",
    residence_country: "Italia",
    phone: "",
    email: "",
    fiscal_code: "",
    license_number: "",
    license_category: "B",
    license_issue_date: "",
    license_issuing_country: "",
    license_expiry: "",
    id_document_type: "carta_identita",
    id_document_number: "",
    id_document_issue_date: "",
    id_document_issuer: "",
    id_document_expiry: "",
    extra_helmet_passenger: false,
    accept_terms: false,
    accept_privacy: false,
  });
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [dataConfirmed, setDataConfirmed] = useState(false);
  const [idCardPhoto, setIdCardPhoto] = useState(null);
  const [idCardPhotoBack, setIdCardPhotoBack] = useState(null);
  const [idExtracting, setIdExtracting] = useState(false);
  const [idAutofilled, setIdAutofilled] = useState(false);
  const idExtractedFor = useRef({ front: null, back: null });

  // Runs OCR on a single side of the ID card and merges the extracted fields into
  // the contract form. Never overrides fields the customer has already edited.
  const extractIdSide = React.useCallback(async (photo, side) => {
    if (!photo || idExtractedFor.current[side] === photo) return;
    idExtractedFor.current[side] = photo;
    setIdExtracting(true);
    try {
      const { data } = await api.post("/id-card/extract", { photo, side });
      if (!data.is_id_document || !data.readable) {
        toast.error(
          side === "back"
            ? (t.contract?.id_back_unreadable || "Foto retro non leggibile: riprova")
            : (t.contract?.id_unreadable || "Foto documento non leggibile: riprova")
        );
        return;
      }
      setForm(prev => {
        const next = { ...prev };
        const fill = (k, v) => { if (!next[k] && v) next[k] = v; };
        fill("family_name", data.family_name);
        fill("given_names", data.given_names);
        fill("birthdate", data.birthdate);
        fill("birthplace", data.birthplace);
        fill("fiscal_code", data.fiscal_code);
        fill("residence_street", data.residence_street);
        fill("residence_number", data.residence_number);
        fill("residence_city", data.residence_city);
        fill("residence_zip", data.residence_zip);
        fill("residence_province", data.residence_province);
        if ((!next.residence_country || next.residence_country === "Italia") && data.residence_country) {
          next.residence_country = data.residence_country;
        }
        fill("id_document_number", data.document_number);
        fill("id_document_issue_date", data.issue_date);
        fill("id_document_issuer", data.issuing_authority);
        fill("id_document_expiry", data.expiry_date);
        if (data.document_type && data.document_type !== "other") {
          next.id_document_type = data.document_type;
        }
        return next;
      });
      setIdAutofilled(true);
      toast.success(
        side === "back"
          ? (t.contract?.id_back_autofilled || "Dati del retro estratti ✓")
          : (t.contract?.id_autofilled || "Dati estratti dal documento ✓ verifica e correggi se serve")
      );
    } catch (e) {
      if (e.response?.status === 422) {
        toast.error(e.response?.data?.detail || "Documento non leggibile");
      } else if (e.response?.status !== 400) {
        console.warn(`id-card extract (${side}) failed:`, e.response?.data?.detail || e.message);
      }
    } finally {
      setIdExtracting(false);
    }
  }, [t]);

  useEffect(() => { if (idCardPhoto) extractIdSide(idCardPhoto, "front"); }, [idCardPhoto, extractIdSide]);
  useEffect(() => { if (idCardPhotoBack) extractIdSide(idCardPhotoBack, "back"); }, [idCardPhotoBack, extractIdSide]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPrefilling(true);
      try {
        const { data } = await api.get(`/bookings/${bookingId}/contract/prefill`);
        if (cancelled) return;
        if (data.signed) setSigned(true);
        setSummary(data.booking_summary);
        setForm(f => ({
          ...f,
          family_name: data.prefill.family_name || "",
          given_names: data.prefill.given_names || "",
          birthplace: data.prefill.birthplace || "",
          birthdate: data.prefill.birthdate || "",
          license_number: data.prefill.license_number || "",
          license_category: (data.prefill.license_category || "B").split(/[\s,]+/)[0].toUpperCase() || "B",
          license_issue_date: data.prefill.license_issue_date || "",
          license_issuing_country: data.prefill.license_issuing_country || "",
          license_expiry: data.prefill.license_expiry || "",
          phone: data.booking_summary?.customer_phone || "",
          email: data.booking_summary?.customer_email || "",
          // If booking has helmets > qty, they've added passenger helmet(s)
          extra_helmet_passenger: (data.booking_summary?.helmets_total || 0) > (data.booking_summary?.quantity || 1),
        }));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore caricamento contratto");
      } finally {
        setLoading(false);
        setPrefilling(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, setSigned]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const requiredFilled = () => {
    const req = [
      "family_name", "given_names", "birthplace", "birthdate",
      "residence_street", "residence_number", "residence_city", "residence_zip", "residence_province", "residence_country",
      "phone", "email", "fiscal_code",
      "license_number", "license_category", "license_issuing_country", "license_expiry",
      "id_document_number", "id_document_issuer", "id_document_expiry",
    ];
    return req.every(k => (form[k] || "").toString().trim().length > 0);
  };

  const canSubmit = signatureDataUrl && form.accept_terms && form.accept_privacy && dataConfirmed && requiredFilled() && previewed;

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      // Send the same form payload but WITHOUT signature; the backend generates
      // a watermarked "ANTEPRIMA" PDF using the customer's uploaded template.
      const payload = { ...form, signature_data_url: "" };
      const isTouch = window.matchMedia("(pointer: coarse)").matches;
      if (isTouch) {
        // Mobile browsers can't render PDFs in an iframe → show server-rendered page images
        const res = await api.post(`/bookings/${bookingId}/contract/preview-images`, payload);
        setPreviewPages(res.data.pages || []);
      } else {
        const res = await api.post(
          `/bookings/${bookingId}/contract/preview`,
          payload,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setPreviewUrl(url);
      }
      setPreviewed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.contract?.preview_failed || "Impossibile generare l'anteprima");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewPages(null);
  };

  const submit = async () => {
    if (!canSubmit) {
      if (!previewed) {
        toast.error(t.contract?.preview_first || "Visualizza l'anteprima del contratto prima di firmarlo");
      } else {
        toast.error(t.contract?.fill_required || "Compila tutti i campi obbligatori, firma e accetta i termini");
      }
      return;
    }
    setSubmittingContract(true);
    try {
      const payload = { ...form, signature_data_url: signatureDataUrl };
      const { data } = await api.post(`/bookings/${bookingId}/contract`, payload);
      if (data.ok) {
        setSigned(true);
        toast.success(t.contract?.signed_ok || "Contratto firmato ✓");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore firma contratto");
    } finally {
      setSubmittingContract(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-zinc-500" data-testid="contract-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        <div className="text-xs uppercase tracking-wider">{t.contract?.loading || "Caricamento dati contratto…"}</div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="space-y-4" data-testid="contract-signed">
        <div className="p-6 border border-green-500/50 bg-green-500/5 text-center">
          <div className="w-12 h-12 mx-auto bg-green-500 text-black flex items-center justify-center mb-3">
            <Check className="w-6 h-6" />
          </div>
          <div className="text-green-300 font-bold text-lg mb-1">{t.contract?.signed_title || "Contratto firmato"}</div>
          <p className="text-sm text-zinc-400">{t.contract?.signed_desc || "Puoi procedere al pagamento sicuro tramite Stripe."}</p>
          <a
            href={`${api.defaults.baseURL}/bookings/${bookingId}/contract/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-xs text-scooter-yellow uppercase tracking-wider font-bold hover:underline"
            data-testid="contract-download-btn"
          >
            <FileText className="w-3 h-3" /> {t.contract?.download || "Scarica PDF"}
          </a>
        </div>
        <button
          onClick={onProceed}
          disabled={submitting}
          className="w-full h-14 bg-scooter-yellow text-black font-bold text-sm tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="contract-proceed-pay-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
          {t.book.continue_pay} · €{total.toFixed(2)}
        </button>
      </div>
    );
  }

  const inputCls = "mt-1.5 bg-transparent border-white/20 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white text-sm h-10";
  const smallLabel = "text-[9px] tracking-[0.25em] uppercase font-bold text-zinc-500";

  return (
    <div className="space-y-5" data-testid="contract-step">
      {/* Header */}
      <div className="border-l-2 border-scooter-yellow pl-3">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow flex items-center gap-2">
          <FileText className="w-3 h-3" /> {t.contract?.title || "Contratto di noleggio"}
        </div>
        <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
          {t.contract?.subtitle || "Compila e firma il contratto. Dopo la firma potrai procedere al pagamento sicuro."}
        </p>
      </div>

      {/* AI-prefilled banner */}
      {prefilling ? (
        <div className="text-xs text-zinc-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Caricamento…</div>
      ) : (
        <div className="text-[11px] text-scooter-yellow/90 bg-scooter-yellow/5 border border-scooter-yellow/20 p-2.5 flex items-start gap-2" data-testid="contract-ai-banner">
          <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{t.contract?.ai_prefill || "I dati della patente sono stati precompilati con AI. Verifica e modifica se necessario."}</span>
        </div>
      )}

      {/* ID card photo autofill — save the customer from typing everything by hand */}
      <div className="border border-scooter-yellow/40 bg-scooter-yellow/[0.05] p-4 sm:p-5" data-testid="id-autofill-box">
        <div className="flex items-start gap-3">
          <IdCard className="w-5 h-5 text-scooter-yellow flex-shrink-0 mt-0.5" strokeWidth={2.2} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow" data-testid="id-autofill-label">
                {t.contract?.id_autofill_label || "Compila con la carta d'identità"}
              </div>
              {idAutofilled && (
                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold text-scooter-yellow border border-scooter-yellow/50 px-2 py-0.5" data-testid="id-autofilled-badge">
                  <Sparkles className="w-3 h-3" strokeWidth={2.4} />
                  {t.contract?.id_autofilled_badge || "Compilato dal documento"}
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-300 leading-relaxed mb-3">
              {t.contract?.id_autofill_desc || "Scatta o carica una foto del fronte E del retro della tua carta d'identità: compileremo automaticamente tutti i dati (residenza, codice fiscale, numero documento…)."}
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <FileSlot
                id="id-card-photo-front"
                value={idCardPhoto}
                onChange={setIdCardPhoto}
                label={t.contract?.id_photo_label_front || "Fronte"}
                testid="contract-id-card-front"
              />
              <FileSlot
                id="id-card-photo-back"
                value={idCardPhotoBack}
                onChange={setIdCardPhotoBack}
                label={t.contract?.id_photo_label_back || "Retro"}
                testid="contract-id-card-back"
              />
            </div>
            {idExtracting && (
              <div className="mt-2 flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-scooter-yellow" data-testid="id-extracting">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t.contract?.id_extracting || "Estrazione dati…"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section A: personal */}
      <Section title={t.contract?.sec_personal || "Dati anagrafici"}>
        <Grid2>
          <Field label={t.contract?.family_name || "Cognome"} value={form.family_name} onChange={v => setField("family_name", v)} testId="contract-family-name" />
          <Field label={t.contract?.given_names || "Nome"} value={form.given_names} onChange={v => setField("given_names", v)} testId="contract-given-names" />
        </Grid2>
        <Grid2>
          <Field label={t.contract?.birthplace || "Luogo di nascita"} value={form.birthplace} onChange={v => setField("birthplace", v)} testId="contract-birthplace" />
          <Field label={t.contract?.birthdate || "Data di nascita"} type="date" value={form.birthdate} onChange={v => setField("birthdate", v)} testId="contract-birthdate" />
        </Grid2>
      </Section>

      {/* Section B: residence */}
      <Section title={t.contract?.sec_residence || "Residenza"}>
        <Grid3>
          <div className="col-span-2"><Field label={t.contract?.street || "Via"} value={form.residence_street} onChange={v => setField("residence_street", v)} testId="contract-street" /></div>
          <Field label={t.contract?.number || "N°"} value={form.residence_number} onChange={v => setField("residence_number", v)} testId="contract-number" />
        </Grid3>
        <Grid3>
          <Field label={t.contract?.city || "Città"} value={form.residence_city} onChange={v => setField("residence_city", v)} testId="contract-city" />
          <Field label={t.contract?.zip || "CAP"} value={form.residence_zip} onChange={v => setField("residence_zip", v)} testId="contract-zip" />
          <Field label={t.contract?.province || "Provincia"} value={form.residence_province} onChange={v => setField("residence_province", v)} testId="contract-province" />
        </Grid3>
        <Grid2>
          <Field label={t.contract?.country || "Nazione"} value={form.residence_country} onChange={v => setField("residence_country", v)} testId="contract-country" />
          <Field label={t.contract?.fiscal_code || "Codice fiscale"} value={form.fiscal_code} onChange={v => setField("fiscal_code", v.toUpperCase())} testId="contract-fiscal-code" />
        </Grid2>
        <Grid2>
          <Field label={t.contract?.phone || "Cellulare"} value={form.phone} onChange={v => setField("phone", v)} testId="contract-phone" />
          <Field label={t.contract?.email || "Email"} type="email" value={form.email} onChange={v => setField("email", v)} testId="contract-email" />
        </Grid2>
      </Section>

      {/* Section C: license */}
      <Section title={t.contract?.sec_license || "Dati patente"}>
        <Grid3>
          <div className="col-span-2"><Field label={t.contract?.license_number || "N° patente"} value={form.license_number} onChange={v => setField("license_number", v.toUpperCase())} testId="contract-lic-number" /></div>
          <div>
            <Label className={smallLabel}>{t.contract?.license_category || "Categoria"}</Label>
            <select
              value={form.license_category}
              onChange={e => setField("license_category", e.target.value)}
              className="mt-1.5 w-full bg-transparent border border-white/20 text-white text-sm h-10 px-2 focus:border-scooter-yellow focus:ring-1 focus:ring-scooter-yellow rounded-none"
              data-testid="contract-lic-category"
            >
              {["A", "A1", "A2", "B", "B1", "C", "D", "E"].map(c => (
                <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>
              ))}
            </select>
          </div>
        </Grid3>
        <Grid3>
          <Field label={t.contract?.license_issue_date || "Data rilascio"} type="date" value={form.license_issue_date} onChange={v => setField("license_issue_date", v)} testId="contract-lic-issue" />
          <Field label={t.contract?.license_issuing_country || "Paese rilascio"} value={form.license_issuing_country} onChange={v => setField("license_issuing_country", v)} testId="contract-lic-country" />
          <Field label={t.contract?.license_expiry || "Scadenza"} type="date" value={form.license_expiry} onChange={v => setField("license_expiry", v)} testId="contract-lic-expiry" />
        </Grid3>
      </Section>

      {/* Section D: ID document */}
      <Section title={t.contract?.sec_id || "Documento di identità"}>
        <Grid2>
          <div>
            <Label className={smallLabel}>{t.contract?.id_type || "Tipo"}</Label>
            <select
              value={form.id_document_type}
              onChange={e => setField("id_document_type", e.target.value)}
              className="mt-1.5 w-full bg-transparent border border-white/20 text-white text-sm h-10 px-2 focus:border-scooter-yellow focus:ring-1 focus:ring-scooter-yellow rounded-none"
              data-testid="contract-id-type"
            >
              <option value="carta_identita" className="bg-[#0a0a0a]">{t.contract?.id_type_id_card || "Carta d'identità"}</option>
              <option value="passaporto" className="bg-[#0a0a0a]">{t.contract?.id_type_passport || "Passaporto"}</option>
            </select>
          </div>
          <Field label={t.contract?.id_number || "Numero"} value={form.id_document_number} onChange={v => setField("id_document_number", v)} testId="contract-id-number" />
        </Grid2>
        <Grid3>
          <Field label={t.contract?.id_issue_date || "Data rilascio"} type="date" value={form.id_document_issue_date} onChange={v => setField("id_document_issue_date", v)} testId="contract-id-issue" />
          <Field label={t.contract?.id_issuer || "Rilasciato da"} value={form.id_document_issuer} onChange={v => setField("id_document_issuer", v)} testId="contract-id-issuer" />
          <Field label={t.contract?.id_expiry || "Scadenza"} type="date" value={form.id_document_expiry} onChange={v => setField("id_document_expiry", v)} testId="contract-id-expiry" />
        </Grid3>
      </Section>

      {/* Section E: rental summary (read-only) */}
      {summary && (
        <Section title={t.contract?.sec_rental || "Riepilogo noleggio"}>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <ReadOnly label={t.book.start} value={summary.start_date} />
            <ReadOnly label={t.book.end} value={summary.end_date} />
            <ReadOnly
              label={t.contract?.scooters || "Scooter"}
              value={
                (summary.units || []).length > 0
                  ? summary.units.map(u => u.plate ? `${u.public_name} (${t.contract?.plate || "targa"} ${u.plate})` : u.public_name).join(", ")
                  : (summary.unit_names || []).join(", ") || `${summary.quantity}× NIU NQiX 500`
              }
            />
            <ReadOnly label={t.book.total} value={`€${(summary.total_price || 0).toFixed(2)}`} highlight />
          </div>
        </Section>
      )}

      {/* Preview */}
      <div className="border border-scooter-yellow/40 bg-scooter-yellow/5 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-scooter-yellow flex-shrink-0 mt-0.5" strokeWidth={2.2} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow mb-1" data-testid="preview-label">
              {t.contract?.preview_label || "Anteprima contratto"}
            </div>
            <div className="text-xs text-zinc-300 leading-relaxed">
              {t.contract?.preview_desc || "Visualizza il contratto completo con i tuoi dati inseriti PRIMA di firmarlo. Obbligatorio."}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={openPreview}
          disabled={previewLoading || !requiredFilled()}
          className="mt-3 w-full h-11 border-2 border-scooter-yellow text-scooter-yellow font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="contract-preview-btn"
        >
          {previewLoading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : previewed ? <Check className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {previewLoading
            ? (t.contract?.preview_loading || "Generazione anteprima…")
            : previewed
              ? (t.contract?.preview_review_again || "Rivedi anteprima")
              : (t.contract?.preview_open || "Mostra anteprima del contratto")}
        </button>
        {!requiredFilled() && (
          <div className="text-[11px] text-zinc-500 mt-2 text-center" data-testid="preview-need-fields">
            {t.contract?.preview_need_fields || "Completa prima i campi obbligatori sopra."}
          </div>
        )}
      </div>

      {/* Signature */}
      <Section title={t.contract?.sec_sign || "Firma"}>
        <SignaturePad
          onSignatureChange={setSignatureDataUrl}
          label={t.contract?.sign_here || "Firma qui sotto"}
          clearLabel={t.contract?.sign_clear || "Cancella"}
        />
        {!signatureDataUrl && (
          <p className="text-[11px] text-zinc-500 mt-1.5">{t.contract?.sign_hint || "Firma con il mouse o il dito nell'area bianca."}</p>
        )}
      </Section>

      {/* Acceptance */}
      <div className="space-y-2">
        <label className={`flex items-start gap-3 cursor-pointer p-3 border transition ${dataConfirmed ? "border-scooter-yellow bg-scooter-yellow/5" : "border-white/15 hover:border-white/30"}`} data-testid="contract-confirm-data-label">
          <input
            type="checkbox"
            checked={dataConfirmed}
            onChange={e => setDataConfirmed(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-scooter-yellow flex-shrink-0"
            data-testid="contract-confirm-data"
          />
          <span className={`text-xs leading-relaxed ${dataConfirmed ? "text-white" : "text-zinc-300"}`}>
            {t.contract?.confirm_data || "Confermo che i dati inseriti dalla patente e dalla carta d'identità sono corretti e veritieri."}
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer p-3 border border-white/15 hover:border-white/30" data-testid="contract-accept-terms-label">
          <input
            type="checkbox"
            checked={form.accept_terms}
            onChange={e => setField("accept_terms", e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-scooter-yellow flex-shrink-0"
            data-testid="contract-accept-terms"
          />
          <span className="text-xs text-zinc-300 leading-relaxed">
            {t.contract?.accept_terms || "Accetto specificamente le clausole 2, 4, 5, 7, 9, 10, 11, 12 delle condizioni generali di noleggio (art. 1341 e 1342 c.c.)."}
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer p-3 border border-white/15 hover:border-white/30" data-testid="contract-accept-privacy-label">
          <input
            type="checkbox"
            checked={form.accept_privacy}
            onChange={e => setField("accept_privacy", e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-scooter-yellow flex-shrink-0"
            data-testid="contract-accept-privacy"
          />
          <span className="text-xs text-zinc-300 leading-relaxed">
            {t.contract?.accept_privacy || "Ho letto e accetto l'informativa sul trattamento dei dati personali (GDPR)."}
          </span>
        </label>
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit || submittingContract}
        className="w-full h-14 bg-scooter-yellow text-black font-bold text-sm tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="contract-submit-btn"
      >
        {submittingContract ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {submittingContract ? (t.contract?.signing || "Firma in corso…") : (t.contract?.sign_submit || "Firma il contratto")}
      </button>

      {/* Preview Modal */}
      {(previewUrl || previewPages) && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="contract-preview-modal"
          onClick={closePreview}
        >
          <div
            className="bg-[#0a0a0a] border border-white/10 w-full max-w-4xl h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow">
                  {t.contract?.preview_modal_label || "Anteprima · specimen"}
                </div>
                <div className="text-white font-heading font-black text-lg tracking-tight" data-testid="preview-modal-title">
                  {t.contract?.preview_modal_title || "Rivedi il contratto prima di firmare"}
                </div>
              </div>
              <button
                onClick={closePreview}
                className="w-9 h-9 flex items-center justify-center border border-white/15 hover:bg-white/10 text-zinc-300"
                aria-label="Close"
                data-testid="preview-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {previewPages ? (
              <div className="flex-1 overflow-y-auto bg-zinc-900 p-2 space-y-2" data-testid="preview-modal-pages">
                {previewPages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Pagina ${i + 1}`}
                    className="w-full border border-white/10"
                    data-testid={`preview-page-${i + 1}`}
                  />
                ))}
              </div>
            ) : (
              <iframe
                src={previewUrl}
                title="Contract preview"
                className="flex-1 w-full bg-white"
                data-testid="preview-modal-iframe"
              />
            )}
            <div className="px-4 py-3 border-t border-white/10 flex-shrink-0 flex items-center gap-3">
              <div className="text-xs text-zinc-400 flex-1">
                {t.contract?.preview_modal_hint || "Scorri per rivedere tutte le pagine. Quando sei pronto chiudi questa finestra e firma."}
              </div>
              <button
                onClick={closePreview}
                className="h-10 px-5 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover flex items-center gap-2"
                data-testid="preview-modal-confirm"
              >
                <Check className="w-3 h-3" />
                {t.contract?.preview_modal_confirm || "Ho letto · procedi alla firma"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="border border-white/10 p-4 space-y-3">
    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{title}</div>
    {children}
  </div>
);

const Grid2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
const Grid3 = ({ children }) => <div className="grid grid-cols-3 gap-3">{children}</div>;

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <Label className="text-[9px] tracking-[0.25em] uppercase font-bold text-zinc-500">{label}</Label>
    <Input
      type={type}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      className="mt-1.5 bg-transparent border-white/20 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white text-sm h-10"
      data-testid={testId}
    />
  </div>
);

const ReadOnly = ({ label, value, highlight }) => (
  <div>
    <div className="text-[9px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-1">{label}</div>
    <div className={`text-sm ${highlight ? "text-scooter-yellow font-bold" : "text-white"}`}>{value || "—"}</div>
  </div>
);
